import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Link, Navigate } from "react-router-dom";
import { formatApiErrorDetail } from "@/lib/utils-app";
import Logo from "@/components/app/Logo";

export default function LoginPage() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/scan" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      const status = err?.response?.status;
      const isTimeout = err?.code === "ECONNABORTED" || /timeout/i.test(err?.message || "");
      // Treat 5xx + network/timeout the same — both are transient/retryable
      // and the user doesn't care about the distinction. Auth (4xx except 429)
      // shows the actual server detail.
      if (!status && (isTimeout || err?.message === "Network Error")) {
        setError("Server is busy. Please try again in a few seconds.");
      } else if (status === 429) {
        setError("Too many attempts. Please wait a minute and try again.");
      } else if (status >= 500) {
        setError("Server is busy. Please try again in a few seconds.");
      } else {
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <div className="border-b-4 border-black p-6">
        <Logo size={44} showText className="text-3xl" />
        <p className="font-body text-sm text-neutral-500 mt-1">
          Thrift resale scouting
        </p>
      </div>

      <div className="flex-1 flex flex-col justify-center px-6 pb-12">
        <h2
          className="font-heading text-2xl font-bold tracking-tight mb-8"
          data-testid="login-heading"
        >
          Sign In
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div
              className="border-2 border-black bg-[#FF1744] text-white p-3 font-body text-sm font-medium"
              data-testid="login-error"
            >
              {error}
            </div>
          )}

          <div>
            <label className="block font-body text-xs font-semibold tracking-[0.1em] uppercase mb-2">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border-2 border-black bg-white p-4 font-body text-lg focus:outline-none focus:ring-4 focus:ring-black/20 transition-all"
              required
              data-testid="login-email-input"
            />
          </div>

          <div>
            <label className="block font-body text-xs font-semibold tracking-[0.1em] uppercase mb-2">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full border-2 border-black bg-white p-4 font-body text-lg focus:outline-none focus:ring-4 focus:ring-black/20 transition-all"
              required
              data-testid="login-password-input"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-black text-white p-4 font-heading font-bold text-lg uppercase tracking-wide border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] active:translate-y-[2px] active:translate-x-[2px] active:shadow-[0px_0px_0px_0px_rgba(0,0,0,1)] transition-all disabled:opacity-50"
            data-testid="login-submit-btn"
          >
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>

        <p className="mt-6 text-center font-body text-sm">
          Don't have an account?{" "}
          <Link
            to="/register"
            className="font-semibold underline underline-offset-4"
            data-testid="login-register-link"
          >
            Create one
          </Link>
        </p>
      </div>
    </div>
  );
}
